import { useState } from "react";
import { useForm, ValidationError } from "@formspree/react";
import { format } from "date-fns";
import { CalendarIcon, Send, Loader2 } from "lucide-react";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { Calendar } from "../ui/calendar";

const SERVICE_OPTIONS = [
  "Honing",
  "Polishing",
  "Sealing",
  "Crack & Chip Repair",
  "Stain & Etch Removal",
  "Grout Cleaning & Color",
  "Full Restoration",
  "Maintenance Program",
  "Not sure — please advise",
];

export default function AppointmentForm() {
  const [state, handleFormspreeSubmit] = useForm("mnjrewpe");
  const [date, setDate] = useState(undefined);
  const [projectType, setProjectType] = useState("Residential");

  const onSubmit = (e) => {
    // Inject the date and project_type hidden values before Formspree handles it
    handleFormspreeSubmit(e);
  };

  if (state.succeeded) {
    return (
      <div className="text-center py-10">
        <p className="font-serif text-2xl text-stone-900 mb-3">Request received.</p>
        <p className="text-stone-600 text-sm">
          We'll be in touch within one business day.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6">

      {/* Hidden fields passed to Formspree */}
      <input type="hidden" name="project_type" value={projectType} />
      <input type="hidden" name="preferred_date" value={date ? format(date, "EEEE, MMMM d, yyyy") : "Not specified"} />

      {/* Project type toggle */}
      <div className="md:col-span-2 flex gap-2">
        {["Residential", "Commercial"].map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => setProjectType(t)}
            className={`flex-1 py-3 text-xs tracking-[0.2em] uppercase border transition-colors ${
              projectType === t
                ? "bg-stone-900 text-white border-stone-900"
                : "bg-white text-stone-900 border-stone-300 hover:border-stone-900"
            }`}
          >
            {t}
          </button>
        ))}
      </div>

      {/* Name */}
      <div className="space-y-2">
        <Label htmlFor="name" className="text-[11px] tracking-[0.2em] uppercase text-stone-600">Name</Label>
        <Input
          id="name"
          name="name"
          placeholder="Jane Doe"
          className="h-12 rounded-none border-stone-300 focus-visible:ring-stone-900"
          required
        />
        <ValidationError field="name" errors={state.errors} className="text-xs text-red-500" />
      </div>

      {/* Email */}
      <div className="space-y-2">
        <Label htmlFor="email" className="text-[11px] tracking-[0.2em] uppercase text-stone-600">Email</Label>
        <Input
          id="email"
          name="email"
          type="email"
          placeholder="you@example.com"
          className="h-12 rounded-none border-stone-300 focus-visible:ring-stone-900"
          required
        />
        <ValidationError field="email" errors={state.errors} className="text-xs text-red-500" />
      </div>

      {/* Phone */}
      <div className="space-y-2">
        <Label htmlFor="phone" className="text-[11px] tracking-[0.2em] uppercase text-stone-600">Phone</Label>
        <Input
          id="phone"
          name="phone"
          type="tel"
          placeholder="(914) 555-0100"
          className="h-12 rounded-none border-stone-300 focus-visible:ring-stone-900"
          required
        />
        <ValidationError field="phone" errors={state.errors} className="text-xs text-red-500" />
      </div>

      {/* Service */}
      <div className="space-y-2">
        <Label className="text-[11px] tracking-[0.2em] uppercase text-stone-600">Service</Label>
        <Select name="service">
          <SelectTrigger className="h-12 rounded-none border-stone-300 focus:ring-stone-900">
            <SelectValue placeholder="Choose a service" />
          </SelectTrigger>
          <SelectContent className="rounded-none">
            {SERVICE_OPTIONS.map((s) => (
              <SelectItem key={s} value={s}>{s}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Preferred Date */}
      <div className="space-y-2 md:col-span-2">
        <Label className="text-[11px] tracking-[0.2em] uppercase text-stone-600">Preferred Date</Label>
        <Popover>
          <PopoverTrigger asChild>
            <button
              type="button"
              className="w-full h-12 px-3 border border-stone-300 bg-white text-left flex items-center justify-between text-sm text-stone-900 hover:border-stone-900 transition-colors"
            >
              <span className={date ? "" : "text-stone-400"}>
                {date ? format(date, "EEEE, MMMM d, yyyy") : "Select a preferred date"}
              </span>
              <CalendarIcon className="w-4 h-4 text-stone-500" />
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0 rounded-none" align="start">
            <Calendar
              mode="single"
              selected={date}
              onSelect={setDate}
              disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))}
              initialFocus
            />
          </PopoverContent>
        </Popover>
      </div>

      {/* Message */}
      <div className="space-y-2 md:col-span-2">
        <Label htmlFor="message" className="text-[11px] tracking-[0.2em] uppercase text-stone-600">Project Details</Label>
        <Textarea
          id="message"
          name="message"
          placeholder="Tell us about your stone — type, location, what you'd like restored…"
          className="min-h-32 rounded-none border-stone-300 focus-visible:ring-stone-900"
        />
        <ValidationError field="message" errors={state.errors} className="text-xs text-red-500" />
      </div>

      {/* Submit */}
      <div className="md:col-span-2">
        <button
          type="submit"
          disabled={state.submitting}
          className="w-full inline-flex items-center justify-center gap-2 h-14 bg-stone-900 text-white text-sm tracking-[0.2em] uppercase hover:bg-stone-800 disabled:opacity-60 transition-colors"
        >
          {state.submitting ? (
            <><Loader2 className="w-4 h-4 animate-spin" /> Sending…</>
          ) : (
            <><Send className="w-4 h-4" /> Request Appointment</>
          )}
        </button>
        <p className="mt-3 text-xs text-stone-500 text-center">
          You can also reach us at{" "}
          <a href="mailto:thestoneremedy@gmail.com" className="underline underline-offset-4 text-stone-900">thestoneremedy@gmail.com</a>.
        </p>
      </div>

    </form>
  );
}
